/**
 * CreateActivityAssistant — Agent-driven chip flow for activity creation
 *
 * Replaces the traditional multi-field form (CreatePostForm) with a guided,
 * conversational flow powered by chips and free-text NLP extraction.
 *
 * Flow: Expression libre + chips → Date → Lieu → Participants → Confirm Card
 *
 * Designed to be called from:
 *   - Raskmote web (authenticated, default mode)
 *   - Locallyia (anonymous, external mode — future, via props)
 *
 * @ticket RAS-57
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FiArrowLeft, FiEdit2 } from 'react-icons/fi';
import { useCategories } from '../../../../shared/hooks/useCategories';
import { useCreatePost } from '../../hooks/useCreatePost';
import { useAgentCreation } from '../../hooks/useAgentCreation';
import { useNotification } from '../../../../shared/hooks/useNotification';
import LocationInput from '../form-fields/LocationInput';
import { extractFieldsFromText } from './extractFieldsFromText';
import { logger } from '../../../../shared/utils/logger';
import { toLocalDateTimeString } from '../../../../shared/utils/date/DateExtensions';
import './styles.css';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Default activity ideas shown on the initial screen (category is internal).
 *  Labels & descriptions come from i18n: creation_assistant.ideas.<id> */
const DEFAULT_ACTIVITY_IDEAS = [
  { id: 'sport_foot', icon: '\u26BD', category: 'sport', subcategory: 'team_sports' },
  { id: 'outing_apero', icon: '\uD83C\uDF77', category: 'outing', subcategory: 'bar' },
  { id: 'sport_rando', icon: '\uD83E\uDD7E', category: 'sport', subcategory: 'running' },
  { id: 'culture_cine', icon: '\uD83C\uDFAC', category: 'culture', subcategory: 'cinema' },
  { id: 'cooking_atelier', icon: '\uD83D\uDC68\u200D\uD83C\uDF73', category: 'cooking' },
  { id: 'meeting_cafe', icon: '\u2615', category: 'meeting', subcategory: 'coffee' },
  { id: 'leisure_jeux', icon: '\uD83C\uDFB2', category: 'leisure', subcategory: 'board_games' },
  { id: 'kids_famille', icon: '\uD83D\uDC68\u200D\uD83D\uDC69\u200D\uD83D\uDC67', category: 'kids' },
  { id: 'transport_covoit', icon: '\uD83D\uDE97', category: 'transport', subcategory: 'carpool' },
  { id: 'outing_picnic', icon: '\uD83E\uDDFA', category: 'outing', subcategory: 'picnic' },
];

const PARTICIPANT_CHIPS = [
  { label: '2 \u2013 4', value: '4' },
  { label: '5 \u2013 8', value: '8' },
  { label: '10+', value: '10' },
  { id: 'unlimited', value: 'unlimited' },
];

const STEPS = ['initial', 'date', 'location', 'participants', 'confirm'];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Get a local datetime string suitable for the datetime-local input `min` attribute.
 *  Delegates to the shared toLocalDateTimeString utility. */
const getLocalDateTimeMin = () => toLocalDateTimeString(new Date());

/** Generate contextual date chips based on current time */
const generateDateChips = (t, locale) => {
  const now = new Date();
  const hour = now.getHours();
  const chips = [];

  if (hour < 18) {
    const d = new Date(now);
    d.setHours(19, 0, 0, 0);
    chips.push({ label: t('creation_assistant.date.tonight'), value: d.toISOString(), icon: '\uD83C\uDF06' });
  }

  const tmNoon = new Date(now);
  tmNoon.setDate(tmNoon.getDate() + 1);
  tmNoon.setHours(12, 0, 0, 0);
  chips.push({ label: t('creation_assistant.date.tomorrow_noon'), value: tmNoon.toISOString(), icon: '\u2600\uFE0F' });

  const tmEve = new Date(now);
  tmEve.setDate(tmEve.getDate() + 1);
  tmEve.setHours(19, 0, 0, 0);
  chips.push({ label: t('creation_assistant.date.tomorrow_evening'), value: tmEve.toISOString(), icon: '\uD83C\uDF19' });

  const daysToSat = (6 - now.getDay() + 7) % 7 || 7;
  const sat = new Date(now);
  sat.setDate(sat.getDate() + daysToSat);
  sat.setHours(10, 0, 0, 0);
  const dayName = sat.toLocaleDateString(locale, { weekday: 'long' });
  chips.push({
    label: t('creation_assistant.date.weekend', { day: dayName.charAt(0).toUpperCase() + dayName.slice(1) }),
    value: sat.toISOString(),
    icon: '\uD83D\uDCC5',
  });

  return chips;
};

/** Format an ISO date string to a readable locale-aware label */
const formatDateFull = (iso, locale = 'fr-FR') => {
  if (!iso) return '';
  const d = new Date(iso);
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
};

/** Check whether a location object contains valid (non-null) coordinates */
const hasValidCoordinates = (loc) =>
  loc?.location?.lat != null && loc?.location?.lng != null;

/**
 * Maps agent uiResult.collectedData to the component's collected state format.
 *
 * Backend format:
 *   { title, description, date, location: { address, lat, lng }, category, maxParticipants }
 *
 * Component format:
 *   { description, category, categoryLabel, categoryIcon, date, dateLabel,
 *     location: { address, location: { lat, lng } }, maxParticipants }
 */
const mapAgentDataToCollected = (agentData, fallbackText, categories, i18n, locale) => {
  const patch = {};

  // Description / title
  patch.description = agentData.description || agentData.title || fallbackText;

  // Category — derive label & icon from categories reference data
  if (agentData.category) {
    const cat = categories.find((c) => c.id === agentData.category);
    patch.category = agentData.category;
    patch.categoryLabel = cat?.labels?.[i18n.language] || agentData.category;
    patch.categoryIcon = cat?.icon;
  }

  // Date (agent returns ISO 8601)
  if (agentData.date) {
    patch.date = agentData.date;
    patch.dateLabel = formatDateFull(agentData.date, locale);
  }

  // Location — agent geocodes: { address, lat, lng }
  //           → component:     { address, location: { lat, lng } }
  if (agentData.location) {
    if (agentData.location.lat != null && agentData.location.lng != null) {
      patch.location = {
        address: agentData.location.address || agentData.location.formattedAddress,
        location: { lat: agentData.location.lat, lng: agentData.location.lng },
      };
    } else if (agentData.location.address) {
      // Agent found address text but couldn't geocode — user will refine at location step
      patch.location = { address: agentData.location.address, location: null };
    }
  }

  // Max participants — only set when explicitly provided by the agent.
  // If absent, keep the collected value as null so the participants step is not skipped.
  if (agentData.maxParticipants != null) {
    patch.maxParticipants = String(agentData.maxParticipants);
  }

  return patch;
};

/** Build the initial collected state from sourceEvent (for external/Locallyia mode) */
const buildInitialCollected = (sourceEvent, initialContent, locale) => {
  const base = {
    description: null,
    category: null,
    categoryLabel: null,
    categoryIcon: null,
    subcategory: null,
    date: null,
    dateLabel: null,
    location: null,
    maxParticipants: null,
  };

  if (sourceEvent) {
    if (sourceEvent.title) base.description = sourceEvent.title;
    if (sourceEvent.date) {
      base.date = sourceEvent.date;
      base.dateLabel = formatDateFull(sourceEvent.date, locale);
    }
    if (sourceEvent.location?.address) {
      base.location = {
        address: sourceEvent.location.address,
        location: sourceEvent.location.lat != null && sourceEvent.location.lng != null
          ? { lat: sourceEvent.location.lat, lng: sourceEvent.location.lng }
          : null,
      };
    }
    if (sourceEvent.category) base.category = sourceEvent.category;
  } else if (initialContent) {
    base.description = initialContent;
  }

  return base;
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** A single row in the confirm card (editable, keyboard-accessible) */
const ConfirmRow = ({ icon, value, onEdit }) => (
  <div
    className="caa-confirm-row"
    onClick={onEdit}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onEdit();
      }
    }}
    role="button"
    tabIndex={0}
  >
    <span className="caa-confirm-icon">{icon}</span>
    <span className="caa-confirm-value">{value}</span>
    <FiEdit2 className="caa-confirm-edit" size={16} />
  </div>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * @param {Object} props
 * @param {Function}  props.onClose           — Close the assistant overlay
 * @param {Function}  [props.onSuccess]       — Callback after successful creation
 * @param {string}    [props.initialContent]  — Pre-filled free text (Raskmote mode)
 * @param {string}    [props.visibility]      — Post visibility ('everyone'|'followers'|'nearby')
 * @param {Object}    [props.sourceEvent]     — Source event for pre-fill (Locallyia mode)
 *   @param {string}  [props.sourceEvent.title]
 *   @param {string}  [props.sourceEvent.date]     — ISO 8601
 *   @param {Object}  [props.sourceEvent.location]  — { address, lat, lng }
 *   @param {string}  [props.sourceEvent.category]
 * @param {Array}     [props.contextualChips] — Override default idea chips (Locallyia mode)
 *   @param {string}  [].id
 *   @param {string}  [].icon
 *   @param {string}  [].category
 * @param {Function}  [props.onAuthRequired]  — Called instead of creating when user is anonymous
 *   Receives the pending activity data so the caller can handle auth gate.
 */
const CreateActivityAssistant = ({
  onClose,
  onSuccess,
  initialContent = '',
  visibility = 'everyone',
  sourceEvent = null,
  contextualChips = null,
  onAuthRequired = null,
}) => {
  const { t, i18n } = useTranslation('posts');
  const locale = i18n.language === 'en' ? 'en-US' : 'fr-FR';
  const { categories } = useCategories();
  const { createPost, loading: creating, error: createError } = useCreatePost({
    onSuccess: () => {
      onSuccess?.();
      onClose();
    },
  });
  const { extractActivity } = useAgentCreation();
  const notification = useNotification('posts');

  // State
  const initialCollected = useMemo(
    () => buildInitialCollected(sourceEvent, initialContent, locale),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const [step, setStep] = useState('initial');
  const [collected, setCollected] = useState(initialCollected);
  const [freeText, setFreeText] = useState(initialContent || sourceEvent?.title || '');
  const [isProcessing, setIsProcessing] = useState(false);
  const [chipsExpanded, setChipsExpanded] = useState(false);

  // If sourceEvent provides enough data, skip straight to confirm
  useEffect(() => {
    if (sourceEvent) {
      const next = getNextStep(initialCollected);
      if (next !== 'initial') setStep(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Which idea chips to show (default or contextual override)
  const ideaChips = contextualChips || DEFAULT_ACTIVITY_IDEAS;

  // ---- Navigation ----

  const getNextStep = useCallback((data) => {
    if (!data.description && !data.category) return 'initial';
    if (!data.date) return 'date';
    if (!hasValidCoordinates(data.location)) return 'location';
    if (data.maxParticipants == null) return 'participants';
    return 'confirm';
  }, []);

  const advance = useCallback(
    (newData) => setStep(getNextStep(newData)),
    [getNextStep]
  );

  const goBack = () => {
    if (step === 'initial') { onClose(); return; }
    const idx = STEPS.indexOf(step);
    if (idx > 0) setStep(STEPS[idx - 1]);
    else onClose();
  };

  // ---- Handlers ----

  const updateAndAdvance = useCallback((patch) => {
    setCollected((prev) => {
      const next = { ...prev, ...patch };
      advance(next);
      return next;
    });
  }, [advance]);

  const handleIdeaChip = (idea) => {
    const cat = categories.find((c) => c.id === idea.category);
    updateAndAdvance({
      description: t(`creation_assistant.ideas.${idea.id}_desc`),
      category: idea.category,
      categoryLabel: cat?.labels?.[i18n.language] || t(`creation_assistant.ideas.${idea.id}`),
      categoryIcon: cat?.icon || idea.icon,
      subcategory: idea.subcategory || null,
    });
  };

  /** Client-side NLP fallback — used when agent is unreachable */
  const fallbackToNlp = useCallback(
    (text) => {
      const extracted = extractFieldsFromText(text, categories);
      if (extracted.date) {
        extracted.dateLabel = formatDateFull(extracted.date, locale);
      }
      if (extracted.category) {
        const cat = categories.find((c) => c.id === extracted.category);
        extracted.categoryLabel = cat?.labels?.[i18n.language] || extracted.category;
      }
      updateAndAdvance({ description: text, ...extracted });
    },
    [categories, locale, i18n.language, updateAndAdvance]
  );

  const handleFreeTextSubmit = async () => {
    if (isProcessing) return;

    const text = freeText.trim();
    if (!text) return;

    setIsProcessing(true);

    try {
      // ---- Agent-first extraction ----
      const { uiResult, metadata } = await extractActivity(text);

      // Case 1: Agent returned structured collectedData (partial or complete)
      if (uiResult?.collectedData) {
        const patch = mapAgentDataToCollected(
          uiResult.collectedData,
          text,
          categories,
          i18n,
          locale
        );
        logger.debug('Agent extracted fields:', patch);
        updateAndAdvance(patch);
        return;
      }

      // Case 2: Agent created the activity directly (all fields present, not blocked)
      if (
        metadata?.toolsUsed?.includes('createActivity') &&
        !metadata?.blocked
      ) {
        logger.debug('Agent created activity directly');
        notification.success('toast.post_created_success');
        onSuccess?.();
        onClose();
        return;
      }

      // Case 3: Agent responded but no structured data — fallback to NLP
      logger.warn('Agent response has no collectedData, falling back to NLP');
      fallbackToNlp(text);
    } catch (agentErr) {
      // ---- Fallback: client-side NLP extraction ----
      logger.warn('Agent extraction failed, falling back to NLP:', agentErr);
      fallbackToNlp(text);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDateChip = (chip) => {
    updateAndAdvance({ date: chip.value, dateLabel: chip.label });
  };

  const handleCustomDate = (e) => {
    const val = e.target.value;
    if (!val) return;
    const d = new Date(val);
    if (d <= new Date()) return;
    updateAndAdvance({ date: d.toISOString(), dateLabel: formatDateFull(d.toISOString(), locale) });
  };

  const handleLocation = (loc) => {
    if (!hasValidCoordinates(loc)) return;
    updateAndAdvance({ location: loc });
  };

  const handleUseMyPosition = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = {
          address: t('creation_assistant.location.my_position'),
          location: { lat: pos.coords.latitude, lng: pos.coords.longitude },
        };
        updateAndAdvance({ location: loc });
      },
      () => { /* GPS denied — stay on step */ }
    );
  };

  const handleParticipantsChip = (chip) => {
    updateAndAdvance({ maxParticipants: chip.value });
  };

  const handleEditField = (field) => {
    const map = { description: 'initial', date: 'date', location: 'location', participants: 'participants' };
    setStep(map[field] || 'initial');
  };

  const handleConfirm = async () => {
    // If caller requires auth first (anonymous/Locallyia mode), delegate
    if (onAuthRequired) {
      onAuthRequired(collected);
      return;
    }

    const selectedCat = categories.find((c) => c.id === collected.category);
    const selectedSub = selectedCat?.subcategories?.find((s) => s.id === collected.subcategory);

    const isUnlimited = collected.maxParticipants === 'unlimited';
    const activity = {
      type: selectedCat?.id || 'other',
      typeLabels: selectedCat?.labels || { fr: 'Autre', en: 'Other' },
      typeIcon: selectedCat?.icon || '\u2753',
      subtype: selectedSub?.id || null,
      subtypeLabels: selectedSub ? { fr: selectedSub.fr, en: selectedSub.en } : null,
      maxParticipants: isUnlimited ? null : (parseInt(collected.maxParticipants, 10) || 8),
      location: collected.location,
      date: new Date(collected.date),
    };

    // useCreatePost handles errors internally (sets error state + shows toast).
    // onClose is called via the onSuccess callback, so the modal stays open on failure.
    await createPost({ content: collected.description, activity, visibility });
  };

  // ---- Breadcrumb ----

  const breadcrumbs = useMemo(() => {
    const items = [];
    if (collected.description) {
      const truncated = collected.description.length > 30
        ? collected.description.slice(0, 30) + '\u2026'
        : collected.description;
      items.push({ icon: collected.categoryIcon || '\uD83D\uDCDD', label: truncated });
    }
    if (collected.dateLabel) items.push({ icon: '\uD83D\uDCC5', label: collected.dateLabel });
    if (collected.location) {
      const locLabel = (collected.location.name || collected.location.address || '').slice(0, 25);
      items.push({ icon: '\uD83D\uDCCD', label: locLabel });
    }
    return items;
  }, [collected]);

  // ---- Escape key to close ----
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // ---- Render ----

  return (
    <div className="caa-overlay" role="dialog" aria-modal="true" aria-labelledby="caa-dialog-title">
      <div className="caa-container">

        {/* Header */}
        {(() => {
          const stepIndex = STEPS.indexOf(step);
          const progressPercent = ((stepIndex + 1) / STEPS.length) * 100;
          return (
            <>
              <div className="caa-header">
                <button className="caa-back" onClick={goBack} aria-label={t('creation_assistant.back')}>
                  <FiArrowLeft size={22} />
                </button>
                <div className="caa-header-center">
                  <h2 id="caa-dialog-title" className="caa-title">
                    {step === 'confirm' ? t('creation_assistant.title_confirm') : t('creation_assistant.title')}
                  </h2>
                  <span className="caa-step-indicator">{stepIndex + 1} / {STEPS.length}</span>
                </div>
                <div className="caa-spacer" />
              </div>

              {/* Progress bar */}
              <div
                className="caa-progress-bar"
                role="progressbar"
                aria-valuemin={1}
                aria-valuemax={STEPS.length}
                aria-valuenow={stepIndex + 1}
              >
                <div className="caa-progress-fill" style={{ width: `${progressPercent}%` }} />
              </div>
            </>
          );
        })()}

        {/* Breadcrumb */}
        {breadcrumbs.length > 0 && step !== 'confirm' && (
          <div className="caa-breadcrumb">
            {breadcrumbs.map((b, i) => (
              <span key={i} className="caa-breadcrumb-item">{b.icon} {b.label}</span>
            ))}
          </div>
        )}

        {/* Steps */}
        <div className="caa-body">

          {/* ---- INITIAL ---- */}
          {step === 'initial' && (
            <div className="caa-step">
              <p className="caa-question">{t('creation_assistant.initial.question')}</p>
              <p className="caa-subtitle">{t('creation_assistant.initial.subtitle')}</p>

              <div className="caa-free-input-wrap">
                <textarea
                  className="caa-free-input"
                  value={freeText}
                  onChange={(e) => setFreeText(e.target.value)}
                  placeholder={t('creation_assistant.initial.placeholder')}
                  rows={3}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleFreeTextSubmit();
                    }
                  }}
                />
                <button
                  className="caa-free-submit"
                  onClick={handleFreeTextSubmit}
                  disabled={!freeText.trim() || isProcessing}
                >
                  {isProcessing ? '\u2026' : '\u27A4'}
                </button>
              </div>

              {isProcessing && (
                <p className="caa-agent-loading" role="status">
                  {t('creation_assistant.initial.agent_loading')}
                </p>
              )}

              <div className="caa-divider"><span>{t('creation_assistant.initial.divider')}</span></div>

              <div className="caa-chips-grid">
                {(chipsExpanded ? ideaChips : ideaChips.slice(0, 6)).map((idea) => (
                  <button key={idea.id} className="caa-idea-chip" onClick={() => handleIdeaChip(idea)}>
                    {idea.icon && (
                      <span className="caa-idea-icon" aria-hidden="true">
                        {idea.icon}
                      </span>
                    )}
                    <span className="caa-idea-label">{t(`creation_assistant.ideas.${idea.id}`)}</span>
                  </button>
                ))}
              </div>
              {ideaChips.length > 6 && (
                <button
                  className="caa-see-more"
                  onClick={() => setChipsExpanded(!chipsExpanded)}
                  aria-expanded={chipsExpanded}
                >
                  {chipsExpanded ? t('creation_assistant.initial.see_less') : t('creation_assistant.initial.see_more', { count: ideaChips.length - 6 })}
                </button>
              )}
            </div>
          )}

          {/* ---- DATE ---- */}
          {step === 'date' && (
            <div className="caa-step">
              <p className="caa-question">{t('creation_assistant.date.question')}</p>
              <p className="caa-subtitle">{t('creation_assistant.date.subtitle')}</p>

              <div className="caa-chips-grid caa-chips-2col">
                {generateDateChips(t, locale).map((chip, i) => (
                  <button key={i} className="caa-chip" onClick={() => handleDateChip(chip)}>
                    <span className="caa-chip-icon">{chip.icon}</span>
                    <span className="caa-chip-label">{chip.label}</span>
                  </button>
                ))}
              </div>

              <div className="caa-custom-section">
                <label htmlFor="caa-date-picker" className="caa-custom-label">{'\uD83D\uDCC5'} {t('creation_assistant.date.custom_label')}</label>
                <input
                  id="caa-date-picker"
                  type="datetime-local"
                  className="caa-date-input"
                  min={getLocalDateTimeMin()}
                  onChange={handleCustomDate}
                />
              </div>
            </div>
          )}

          {/* ---- LOCATION ---- */}
          {step === 'location' && (
            <div className="caa-step">
              <p className="caa-question">{t('creation_assistant.location.question')}</p>
              <p className="caa-subtitle">{t('creation_assistant.location.subtitle')}</p>

              <div className="caa-chips-row">
                <button className="caa-chip" onClick={handleUseMyPosition}>
                  <span className="caa-chip-icon">{'\uD83D\uDCCD'}</span>
                  <span className="caa-chip-label">{t('creation_assistant.location.around_me')}</span>
                </button>
              </div>

              <div className="caa-custom-section">
                {/* Wrap LocationInput inside label for a11y (LocationInput renders a plain <input>) */}
                <label className="caa-custom-label caa-label-wrap">
                  <span>{'\uD83D\uDD0D'} {t('creation_assistant.location.search_label')}</span>
                  <LocationInput
                    value={collected.location}
                    onChange={handleLocation}
                    placeholder={t('creation_assistant.location.search_placeholder')}
                  />
                </label>
              </div>
            </div>
          )}

          {/* ---- PARTICIPANTS ---- */}
          {step === 'participants' && (
            <div className="caa-step">
              <p className="caa-question">{t('creation_assistant.participants.question')}</p>
              <p className="caa-subtitle">{t('creation_assistant.participants.subtitle')}</p>

              <div className="caa-chips-grid caa-chips-4col">
                {PARTICIPANT_CHIPS.map((chip, i) => (
                  <button key={i} className="caa-chip" onClick={() => handleParticipantsChip(chip)}>
                    <span className="caa-chip-label">
                      {chip.id === 'unlimited' ? t('creation_assistant.participants.unlimited') : chip.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ---- CONFIRM ---- */}
          {step === 'confirm' && (() => {
            // Derive category display from categories data when collected label is missing
            // (e.g. sourceEvent.category was set without label/icon)
            const confirmCat = collected.category
              ? categories.find((c) => c.id === collected.category)
              : null;
            const confirmCatLabel = collected.categoryLabel
              || confirmCat?.labels?.[i18n.language]
              || t('creation_assistant.confirm.category_other');
            const confirmCatIcon = collected.categoryIcon
              || confirmCat?.icon
              || '\uD83C\uDFF7\uFE0F';

            // Participants display
            const isUnlimited = collected.maxParticipants === 'unlimited';
            const participantsLabel = isUnlimited
              ? t('creation_assistant.participants.unlimited_label')
              : t('creation_assistant.participants.max_label', { count: collected.maxParticipants });

            return (
            <div className="caa-step">
              <p className="caa-question">{t('creation_assistant.confirm.ready')}</p>
              <p className="caa-subtitle">{t('creation_assistant.confirm.subtitle')}</p>

              <div className="caa-confirm-card">
                <ConfirmRow icon={'\uD83D\uDCDD'} value={collected.description} onEdit={() => handleEditField('description')} />
                <ConfirmRow icon={'\uD83D\uDCC5'} value={collected.dateLabel || formatDateFull(collected.date, locale)} onEdit={() => handleEditField('date')} />
                <ConfirmRow icon={'\uD83D\uDCCD'} value={collected.location?.name || collected.location?.address} onEdit={() => handleEditField('location')} />
                <ConfirmRow icon={'\uD83D\uDC65'} value={participantsLabel} onEdit={() => handleEditField('participants')} />
                <div className="caa-confirm-row caa-confirm-auto">
                  <span className="caa-confirm-icon">{confirmCatIcon}</span>
                  <span className="caa-confirm-value">{confirmCatLabel}</span>
                  <span className="caa-confirm-badge">{t('creation_assistant.confirm.auto_badge')}</span>
                </div>
              </div>

              {createError && (
                <p className="caa-error" role="alert">
                  {typeof createError === 'string' ? createError : createError.message || t('errors.unknown')}
                </p>
              )}

              <button className="caa-cta" onClick={handleConfirm} disabled={creating}>
                {creating ? t('creation_assistant.confirm.submitting') : t('creation_assistant.confirm.submit')}
              </button>

              <button className="caa-cancel" onClick={onClose}>{t('creation_assistant.confirm.cancel')}</button>
            </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default CreateActivityAssistant;
